import { Link } from 'react-router-dom'
import { SEO } from '@/components/SEO'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Brain, TrendingUp, FileText, LayoutGrid, Network, Users, Smartphone, Workflow } from 'lucide-react'

// Feature sections data
const aiFeatures = [
  {
    title: "AI-Driven Knowledge Graphs",
    description: "Visualize your content's connections with intelligent, AI-powered graphs that enhance topical relevance.",
    icon: Brain
  },
  {
    title: "Smart Content Briefs",
    description: "Generate comprehensive content briefs that align with your semantic SEO strategy.",
    icon: FileText
  },
  {
    title: "Algorithmic Authoring",
    description: "Create content that's optimized for both search engines and human readers.",
    icon: Workflow
  },
  {
    title: "Topic Mapping",
    description: "Build comprehensive topic hierarchies that establish your authority in your niche.",
    icon: Network
  }
]

const processSteps = [
  {
    number: 1,
    title: "Topic Discovery",
    description: "Uncover high-value topics and keywords tailored to your niche with AI-driven insights."
  },
  {
    number: 2,
    title: "Content Planning",
    description: "Develop a clear content strategy with detailed topical maps and actionable content briefs."
  },
  {
    number: 3,
    title: "Content Creation",
    description: "Leverage algorithmic authorship tools to create consistent, high-quality, and scalable content."
  },
  {
    number: 4,
    title: "Performance Tracking",
    description: "Monitor your content's performance with real-time analytics and insights."
  }
]

const managementFeatures = [
  {
    title: "Advanced Content Tools",
    description: "Create briefs, maps, and outlines to streamline content strategies.",
    icon: FileText
  },
  {
    title: "Team Collaboration",
    description: "Collaborate live on graphs, briefs, and strategies with your team.",
    icon: Users
  },
  {
    title: "Intuitive Dashboard",
    description: "Monitor and manage your content strategy from a central hub.",
    icon: LayoutGrid
  },
  {
    title: "Progress Tracking",
    description: "Track your content performance and SEO improvements over time.",
    icon: TrendingUp
  },
  {
    title: "Mobile Access",
    description: "Access your workflow and tools from any device, anywhere.",
    icon: Smartphone
  },
  {
    title: "Smart Workflows",
    description: "Automate and streamline your content creation process.",
    icon: Workflow
  }
]

// Helper components
const FeatureCard = ({ title, description, icon: Icon }: {
  title: string
  description: string
  icon: React.ElementType
}) => (
  <Card className="border-primary/10 bg-card/50 backdrop-blur-sm transition-all duration-300 hover:border-primary/20 hover:shadow-lg">
    <CardHeader>
      <Icon className="h-12 w-12 text-primary mb-4" />
      <CardTitle className="text-xl">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <p className="text-muted-foreground">{description}</p>
    </CardContent>
  </Card>
)

const ProcessStep = ({ number, title, description }: {
  number: number
  title: string
  description: string
}) => (
  <div className="flex flex-col items-center text-center">
    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-2xl font-bold mb-4 text-primary">
      {number}
    </div>
    <h3 className="text-xl font-bold mb-2">{title}</h3>
    <p className="text-muted-foreground">{description}</p>
  </div>
)

export default function Features() {
  return (
    <>
      <SEO
        title="Features - WithWords"
        description="Discover the powerful features of WithWords that help you create better content and build topical authority."
      />
      
      <div className="min-h-screen bg-gradient-to-br from-background to-background/90">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:16px]" />
          <div className="container mx-auto px-4 py-24 relative">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl font-bold sm:text-5xl md:text-6xl mb-6 bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/70">
                Powerful Tools for Content Excellence
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Transform your content strategy with our comprehensive suite of AI-powered tools and features designed for modern content creators.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" asChild>
                  <Link to="https://app.withwords.app/signup/">Start Free Trial</Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/contact">Schedule Demo</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* AI Features Section */}
        <section className="py-24 bg-muted/50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">AI-Powered Solutions</h2>
            <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto mb-12">
              Leverage the power of artificial intelligence to create, optimize, and scale your content strategy.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {aiFeatures.map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-24">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Our Streamlined Process</h2>
            <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto mb-12">
              A proven workflow that helps you create high-quality content consistently and efficiently.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {processSteps.map((step, index) => (
                <ProcessStep key={index} {...step} />
              ))}
            </div>
          </div>
        </section>

        {/* Management Features Section */}
        <section className="py-24 bg-muted/50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Powerful Management Features</h2>
            <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto mb-12">
              Everything you need to manage your content strategy effectively.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {managementFeatures.map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Transform Your Content Strategy?</h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join thousands of content creators who are already using WithWords to create impactful content at scale.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" asChild className="w-full sm:w-auto">
                  <Link to="https://app.withwords.app/signup/">Start Free Trial</Link>
                </Button>
                <Button variant="outline" size="lg" asChild className="w-full sm:w-auto">
                  <Link to="/contact">Schedule Demo</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
